/** The quiz's external tab becomes the IDE: no dashboard, reload, or popup. */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { studentPortal } from '../services/api';

const openIDE = (url) => window.location.replace(url);
const POLL_MS = 3000;
const START_TIMEOUT_MS = 5 * 60 * 1000;

export default function StudentIDELaunch({ go = openIDE }) {
  const { labId } = useParams();
  const { token, loading, isAuthenticated, isStudent, authError } = useAuth();
  const [message, setMessage] = useState('Signing you in…');
  const [error, setError] = useState(null);
  const [readyUrl, setReadyUrl] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setError(null);
    setReadyUrl(null);
    if (loading) return undefined;
    if (!isAuthenticated || !isStudent) {
      setError(authError || 'Return to your D2L quiz or course tab and open the lab link as a student.');
      return undefined;
    }
    const controller = new AbortController();
    const options = { signal: controller.signal, skipAuthRedirect: true, timeout: 20000 };
    let cancelled = false;
    let timer;
    const deadline = Date.now() + START_TIMEOUT_MS;

    const launch = async () => {
      setMessage('Opening your lab environment…');
      while (!cancelled) {
        if (Date.now() >= deadline) {
          throw new Error('Your environment is taking longer than usual to start. Try again in a minute.');
        }
        // Read the current token each time: an idle restart can refresh the
        // stored session. The POST performs the authoritative access checks.
        const { lab } = await studentPortal.getLab(labId, options);
        if (cancelled) return;
        if (lab?.isD2LLocked) throw new Error(lab.d2lLockMessage || 'Open this lab through D2L.');
        if (!lab?.isAccessible) throw new Error('This lab is not currently open for student work.');
        if (!lab.container?.sessionToken) {
          throw new Error('Your environment has not been set up yet. Contact your instructor.');
        }
        const result = await studentPortal.authenticateForIDE(labId, lab.container.sessionToken, undefined, options);
        if (cancelled) return;
        if (!result.starting) {
          const url = new URL(result.url);
          if (!['https:', 'http:'].includes(url.protocol) || url.searchParams.has('token') || url.searchParams.has('tkn')) {
            throw new Error('The IDE could not be opened. Return to D2L and try the lab link again.');
          }
          setMessage('Your IDE is ready. Opening it in this tab…');
          setReadyUrl(url.href);
          go(url.href);
          return;
        }
        setMessage(result.message || 'Starting your environment. This takes about a minute.');
        await new Promise((resolve) => { timer = setTimeout(resolve, POLL_MS); });
      }
    };
    launch().catch((err) => {
      if (cancelled) return;
      setError(err.response?.status === 401
        ? 'Your sign-in has expired. Return to D2L and open the lab link again.'
        : err.response?.data?.message || err.response?.data?.error || err.message || 'Unable to open your IDE. Try again.');
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [labId, token, loading, isAuthenticated, isStudent, authError, attempt, go]);

  return (
    <main style={{ maxWidth: 540, margin: '15vh auto', padding: 24, textAlign: 'center' }}>
      <h1 style={{ fontSize: 24, marginBottom: 20 }}>Virtual Labs</h1>
      {!loading && error ? (
        <div role="alert">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Your IDE could not be opened</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>{error}</p>
          {isAuthenticated && isStudent && <button className="btn btn-primary" onClick={() => setAttempt(value => value + 1)}>
            Try again
          </button>}
        </div>
      ) : (
        <div role="status" aria-live="polite">
          <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto 16px' }} aria-hidden="true" />
          <p>{loading ? 'Signing you in…' : message}</p>
        </div>
      )}
      {!loading && readyUrl && !error && <div style={{ marginTop: 24 }}>
        <a href={readyUrl} className="btn btn-secondary">Continue to IDE</a>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
          If LockDown Browser blocks this page, ask your instructor to allow the Virtual Labs domain in the quiz settings.
        </p>
      </div>}
    </main>
  );
}
